// This component is a wrapper around TanStack React Table and centralizes common grid logic.
//https://github.com/TanStack/table/blob/main/README.md
//https://tanstack.com/table/latest/docs/introduction
import { Column, ColumnDef, flexRender, getCoreRowModel, getExpandedRowModel, getPaginationRowModel, getSortedRowModel, Header, Row, useReactTable } from '@tanstack/react-table';
import { CaretDownIcon, CaretUpIcon, CaretUpDownIcon } from './Icons';

export { flexRender, getCoreRowModel, getExpandedRowModel, getPaginationRowModel, getSortedRowModel, useReactTable };

// Generic props for react-table
export type TableProps<TData extends object> = {
  columns: ColumnDef<TData, any>[];
  data: TData[];
  renderRowSubComponent?: (props: { row: Row<TData> }) => React.ReactNode; //optional only used in Alert.tsx
}

export type ColumnHeaderProps = {
  id: string;
  className?: string;
  sortedDescLabel?: string;
  sortedAscLabel?: string;
  unsortedLabel?: string;
}

export function handleTableColumnHeaderKeyDown<TData>(event: React.KeyboardEvent<HTMLTableCellElement>, column: Column<TData, unknown>) {
    event.stopPropagation();
    switch (event.key) {
      case "Enter": 
        column.toggleSorting();
        break;  
      default: break;
    }
};

function renderColumnHeaderContent<TData>(header: Header<TData, unknown>, columnHeaderProps?: ColumnHeaderProps) {
  const column = header.column;
  let icons = <></>;

  if (column.getCanSort()) {
    const sortState = column.getIsSorted();
    const title = getTitle(column, columnHeaderProps);

    if (sortState === 'desc') {
      icons = <CaretUpIcon className="column-header-icon" title={title} />;
    } else if (sortState === 'asc') {
      icons = <CaretDownIcon className="column-header-icon" title={title} />;
    } else {
      icons = <CaretUpDownIcon className="column-header-double-icon" title={title} />;
    }
  }

  const columnHeaderContent = header.isPlaceholder //Placeholder headers are used to fill in the gaps when a column is hidden or when a column is part of a group column.
    ? null
    : flexRender(column.columnDef.header, header.getContext());

  if (columnHeaderProps && columnHeaderProps.className) {
    return <><div className={columnHeaderProps.className}>{columnHeaderContent} {icons}</div></>;
  } else {
    return <>{columnHeaderContent}{icons}</>;
  }
}

function getTitle<TData>(column: Column<TData, unknown>, columnHeaderProps?: ColumnHeaderProps) {
    let title = '';
    if (column.getCanSort()) {
        const sortState = column.getIsSorted();
        if (sortState === 'desc') { // Uses fallback empty string when a label is missing to avoid assigning undefined(avoid typescript error).
            title = columnHeaderProps?.sortedDescLabel ?? '';
        } else if (sortState === 'asc') {
            title = columnHeaderProps?.sortedAscLabel ?? '';
        } else {
            title = columnHeaderProps?.unsortedLabel ?? '';
        }
    }
    return title;
}

export function renderColumnHeader<TData>(header: Header<TData, unknown>, columnHeaderProps?: ColumnHeaderProps, sortedDescendingTitle = undefined, sortedAscendingTitle = undefined) {
  const column = header.column;
  const canSort = column.getCanSort();
  const sortHandler = column.getToggleSortingHandler();
  const title = getTitle(column, columnHeaderProps); 

  // For accessibility reasons, columns with no header content need to be TD and not TH
  if (columnHeaderProps) {
    return (
      <th
        key={header.id}
        tabIndex={canSort ? 0 : -1}
        onClick={sortHandler}
        onKeyDown={(e) => handleTableColumnHeaderKeyDown(e, column)}
        title={title}
        id={column.id}
      >
        {renderColumnHeaderContent(header, columnHeaderProps)}
      </th>
    );
  } else {
    return (
      <td
        key={header.id}
        tabIndex={canSort ? 0 : -1}
        onClick={sortHandler}
        onKeyDown={(e) => handleTableColumnHeaderKeyDown(e, column)}
        id={column.id}
      >
        {renderColumnHeaderContent(header, columnHeaderProps)}
      </td>
    );
  }
}